import { useUserGuids } from '../store/useUserGuids';
import { Channel, Message, User } from '../../type';
import { useCallback } from 'react';
import { CollectionReference, getDocs, query, where } from 'firebase/firestore';
import { chunk } from 'lodash';
import {
  IN_CLAUSE_LIMIT,
  useUserCollection,
} from '../../../../firebase/useCollection';

export const useUserFetcher = () => {
  const collection = useUserCollection();
  const fetchedUserGuids = useUserGuids();
  return async (userGuids: string[]): Promise<User[]> => {
    return await fetchUsers(collection, userGuids, fetchedUserGuids);
  };
};

export const useUserFetcherForChannels = () => {
  const collection = useUserCollection();
  const fetchedUserGuids = useUserGuids();
  return useCallback(
    async (channels: Channel[]): Promise<User[]> => {
      const memberGuids = channels.flatMap((channel) => channel._memberGuids);
      return await fetchUsers(collection, memberGuids, fetchedUserGuids);
    },
    [collection, fetchedUserGuids],
  );
};

export const useUserFetcherForMessages = () => {
  const collection = useUserCollection();
  const fetchedUserGuids = useUserGuids();
  return useCallback(
    async (messages: Message[]): Promise<User[]> => {
      const authorGuids = messages.map((message) => message.authorGuid);
      return await fetchUsers(collection, authorGuids, fetchedUserGuids);
    },
    [collection, fetchedUserGuids],
  );
};

/*================HELPER FUNCTIONS================*/

const fetchUsers = async (
  collection: CollectionReference,
  userGuids: string[],
  fetchedUserGuids: string[],
): Promise<User[]> => {
  const dedupUserGuids = Array.from(new Set(userGuids));
  const userGuidsToFetch = dedupUserGuids.filter(
    (userGuid) => !fetchedUserGuids.includes(userGuid),
  );
  const userPromises = chunk(userGuidsToFetch, IN_CLAUSE_LIMIT).map(
    async (guids) => {
      const userQuery = query(collection, where('guid', 'in', guids));
      const snapshot = await getDocs(userQuery);
      return snapshot.docs.map((doc) => doc.data() as User);
    },
  );
  const userChunks = await Promise.all(userPromises);
  return userChunks.flat();
};
